'use client';

import React, { useMemo } from 'react';
import WidgetHeader from 'src/components/Profiles/shared/widgets/WidgetHeader';
import { Card } from 'src/components/PulseUI';
import WidgetLoading from 'src/components/Profiles/shared/widgets/shared/WidgetLoading';
import WidgetEmpty from 'src/components/Profiles/shared/widgets/shared/WidgetEmpty';
import { useGetTopProvidersPaginatedInfiniteQuery } from 'src/api/redux/profiles/profilesApi';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { useWidgetConfig } from 'src/context/providers/WidgetConfigProvider';
import { getEntityIdentifiers } from 'src/utils/getEntityIdentifiers';
import { useInfiniteScroll } from 'src/hooks/common/useInfiniteScroll';
import { buildTopProvidersQueryParams } from './utils';

const EMPTY_PROVIDERS: Record<string, any>[] = [];

const CompactTopProviders: React.FC = () => {
  const { entityDetails } = useProfile();
  const widgetConfig = useWidgetConfig();

  const { providerId, profileType } = getEntityIdentifiers(entityDetails);

  const codeSetLabel = widgetConfig?.filterSet?.label
    ? `${widgetConfig.filterSet.label} Claims`
    : 'All Claims';

  const filterSet = widgetConfig?.filterSet ?? null;
  const minYear = widgetConfig?.minYear;
  const maxYear = widgetConfig?.maxYear;
  const providerFilterSetId = widgetConfig?.providerFilterSetId;
  const encounterId = widgetConfig?.widget?.encounter?.id ?? null;

  const queryParams = useMemo(
    () => buildTopProvidersQueryParams(filterSet, minYear, maxYear, providerFilterSetId, encounterId),
    [filterSet, minYear, maxYear, providerFilterSetId, encounterId]
  );

  const {
    data: paginatedData,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useGetTopProvidersPaginatedInfiniteQuery(
    { providerId, queryParams },
    { skip: !providerId || !profileType }
  );

  const providers = useMemo(
    () => paginatedData?.pages?.flatMap((page: any) => page.results) ?? EMPTY_PROVIDERS,
    [paginatedData]
  );

  const { fetchTriggerRef } = useInfiniteScroll({
    hasNextPage: !!hasNextPage,
    isFetching: isLoading || !!isFetchingNextPage,
    fetchNextPage,
  });

  return (
    <Card
      className="p-4 flex flex-col gap-2 bg-white h-full"
      data-testid="compact-top-providers"
    >
      <WidgetHeader fallbackTitle={`Top Providers - ${codeSetLabel}`} />

      <div className="flex-1 min-h-0 overflow-y-auto max-h-80">
        {isLoading ? (
          <WidgetLoading data-testid="compact-top-providers-loading" />
        ) : providers.length === 0 ? (
          <WidgetEmpty data-testid="compact-top-providers-empty" variant="full" />
        ) : (
          <div
            className="flex flex-col"
            role="list"
            aria-label="Top Providers"
            data-testid="compact-top-providers-list"
          >
            <div
              className="flex items-center justify-end pb-1 border-b border-neutral-100 sticky top-0 bg-white z-10"
              role="row"
              aria-label="Column headers"
            >
              <span
                className="text-xs font-semibold text-tertiary min-w-20 text-end"
                role="columnheader"
              >
                Claims
              </span>
            </div>
            {providers.map((provider: Record<string, any>, index: number) => (
              <div
                key={`${provider.provider_id}-${index}`}
                className="flex items-center justify-between py-2 border-b border-neutral-100 last:border-b-0"
                role="listitem"
              >
                <div className="flex flex-col min-w-0 flex-1">
                  <span className="text-sm font-medium text-primary truncate">
                    {provider.name || 'N/A'}
                  </span>
                  {provider.specialty && (
                    <span className="text-xs text-secondary truncate">
                      {provider.specialty}
                    </span>
                  )}
                </div>
                <div className="flex flex-col items-end flex-shrink-0 ml-2 px-2 py-1 w-20 min-w-20">
                  <span className="text-sm font-semibold text-teal-900">
                    {provider.claims
                      ? provider.claims === '<11'
                        ? provider.claims
                        : Number(provider.claims).toLocaleString()
                      : '—'}
                  </span>
                  {provider.percentage != null && (
                    <span className="text-xs text-neutral-500">
                      {provider.percentage}%
                    </span>
                  )}
                </div>
              </div>
            ))}
            <div
              ref={fetchTriggerRef}
              className="h-1"
              aria-hidden="true"
              data-testid="compact-top-providers-scroll-trigger"
            />
            {isFetchingNextPage && (
              <WidgetLoading data-testid="compact-top-providers-fetching-next" />
            )}
          </div>
        )}
      </div>

      <p className="text-xs text-neutral-500">
        Prescription data not available in this widget.
      </p>
    </Card>
  );
};

export default CompactTopProviders;
